/**
 * DED-1507: Risk Linked Documents Component
 * 
 * Allows users to add supporting documents to risks.
 * Based on SupportingDocsDropdown from MSG.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, ExternalLink, Trash2, ChevronDown, ChevronUp, Link, Upload, X, Loader2, Check } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUpload, faFileLines } from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTenancy } from '@/contexts/TenancyContext';
import { toast } from 'sonner';
import { getDocument, saveDocument, createDocument } from '@/services/documentsService';
import DocumentPage from '@/app/pages/document-detail';
import { renderDate } from '@shared/renderDate';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DocumentsCombobox, DocumentsProvider } from '@/components/ui/DocumentsCombobox';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getMenuOptions } from '@/services/menusService';

interface LinkedDocument {
  docID: string;
  fileName: string;
  displayName?: string;
  documentType?: string;
  nextReviewDate?: Date | string;
  lastReviewDate?: Date | string;
}

// Wrapper component to properly initialize document state before rendering DocumentPage
const DialogDocumentDetail = ({ 
  prefillData, 
  onSave, 
  onClose 
}: { 
  prefillData: any; 
  onSave: (doc: any) => void; 
  onClose: () => void;
}) => {
  const [document, setDocument] = useState<any>(null);
  const [redirected, setRedirected] = useState(false);
  
  const isEditMode = prefillData?.documentID || prefillData?.isEdit;

  useEffect(() => {
    if (prefillData) {
      // For editing existing document
      if (prefillData.documentID || prefillData.isEdit) {
        setDocument({
          ...prefillData,
        });
      } else {
        // For creating new document - ensure all required fields are set
        setDocument({
          ...prefillData,
          documentType: prefillData.documentType || 'Risk Document',
          status: prefillData.status || 'Draft',
          dateAdded: prefillData.dateAdded || new Date().toISOString(),
        });
      }
    }
  }, [prefillData]);

  // For new documents, open in new tab because DocumentPage uses useParams()
  // which picks up the risk ID from the current URL
  useEffect(() => {
    if (!isEditMode && document && !redirected) {
      setRedirected(true);
      // Store prefill data in sessionStorage and open new document page
      sessionStorage.setItem('documentPrefillData', JSON.stringify(document));
      window.open('/documentdetail/new', '_blank');
      onClose();
    }
  }, [document, isEditMode, onClose, redirected]);

  const handleSave = (savedDoc: any) => {
    onSave(savedDoc);
  };

  if (!document) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  // For edit mode, show the document page in dialog
  if (isEditMode) {
    return (
      <DocumentPage
        documentID={prefillData.documentID}
        prefillData={document}
        onSave={handleSave}
        onCancel={onClose}
        onBack={onClose}
        referer="Risk Register"
      />
    );
  }
  
  // For create mode, show loading while we redirect
  return (
    <div className="flex items-center justify-center h-full">
      <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      <span className="ml-2 text-sm text-muted-foreground">Opening document editor...</span>
    </div>
  );
};

interface RiskLinkedDocumentsProps {
  riskId: string;
  riskOwner?: string; // DED-1964: Risk owner to inherit as document owner
  riskNextReviewDate?: Date | string;
  riskControlIds?: string[]; // DED-1507: Related controls from the risk
  linkedDocuments?: string[];
  onDocumentsChange?: (docIds: string[]) => void;
  isReadOnly?: boolean;
  isCreateMode?: boolean;
}

export function RiskLinkedDocuments({
  riskId,
  riskOwner, // DED-1964: Risk owner to inherit as document owner
  riskNextReviewDate,
  riskControlIds = [], // DED-1507: Related controls from the risk
  linkedDocuments = [],
  onDocumentsChange,
  isReadOnly = false,
  isCreateMode = false
}: RiskLinkedDocumentsProps) {
  const { activeTenancy } = useTenancy();
  const [documents, setDocuments] = useState<LinkedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
  const [prefillData, setPrefillData] = useState<any>(null);
  const [dialogKey, setDialogKey] = useState(0);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [docToUnlink, setDocToUnlink] = useState<string | null>(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedDocsToLink, setSelectedDocsToLink] = useState<string[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    title: '',
    documentType: 'Evidence',
    owner: '',
    comments: '',
  });
  const [documentTypes, setDocumentTypes] = useState<Array<{key: string; value: string}>>([]);
  const [loadingDocTypes, setLoadingDocTypes] = useState(false);

  // Fetch linked documents - accepts optional docIds to fetch (for immediate refresh after linking)
  const fetchDocuments = useCallback(async (docIdsToFetch?: string[]) => {
    const idsToFetch = docIdsToFetch || linkedDocuments;
    
    if (!activeTenancy?.id || idsToFetch.length === 0) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const fetchedDocs: LinkedDocument[] = [];

      // Fetch each linked document using the documents service
      for (const docId of idsToFetch) {
        try {
          const docData = await getDocument(docId, activeTenancy.id);
          if (docData) {
            fetchedDocs.push({
              docID: docId,
              fileName: docData.fileName || docData.title || 'Untitled',
              displayName: (docData as any).displayName || docData.title || docData.fileName,
              documentType: docData.documentType,
              nextReviewDate: docData.nextReviewDate as any,
              lastReviewDate: docData.lastReviewDate as any
            });
          }
        } catch (err) {
          console.error(`Error fetching document ${docId}:`, err);
        }
      }

      setDocuments(fetchedDocs);
    } catch (error) {
      console.error('Error fetching linked documents:', error);
      toast.error('Failed to load linked documents');
    } finally {
      setLoading(false);
    }
  }, [activeTenancy?.id, linkedDocuments]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Fetch document types from menu service
  useEffect(() => {
    const loadDocumentTypes = async () => {
      if (!activeTenancy?.id) return;
      try {
        setLoadingDocTypes(true);
        const types = await getMenuOptions('documentLibrary', 'documentTypes', activeTenancy.id);
        setDocumentTypes(types || []);
      } catch (error) {
        console.error('Error loading document types:', error);
        // Fallback to default types
        setDocumentTypes([
          { key: 'Risk Document', value: 'Risk Document' },
          { key: 'Policy', value: 'Policy' },
          { key: 'Procedure', value: 'Procedure' },
          { key: 'Form', value: 'Form' },
        ]);
      } finally {
        setLoadingDocTypes(false);
      }
    };
    loadDocumentTypes();
  }, [activeTenancy?.id]);

  // DED-1964: Handle file upload with inherited properties
  const handleFileUpload = async () => {
    if (!selectedFile || !activeTenancy?.id) return;

    try {
      setIsUploading(true);
      const today = new Date();
      const oneYearFromToday = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

      // Use form data or defaults
      const title = uploadFormData.title || selectedFile.name.replace(/\.[^/.]+$/, '');
      const documentType = uploadFormData.documentType || 'Evidence';
      const owner = uploadFormData.owner || riskOwner || '';
      const comments = uploadFormData.comments || `Supporting document for risk ${riskId}`;

      // Create document with inherited properties matching document detail page structure
      const documentData = {
        fileName: selectedFile.name,
        displayName: title,
        title: title,
        documentType: documentType,
        status: 'Current',
        classification: 'Business Confidential', // Required field
        uploadedBy: owner, // DED-1964: Inherit owner from risk (uploadedBy is the owner field)
        owner: owner, // Also set owner for compatibility
        lastReviewDate: today, // DED-1964: Set to today
        nextReviewDate: oneYearFromToday, // DED-1964: Default to a year from today
        dateAdded: today,
        dateUploaded: today,
        linkedRisks: [riskId],
        relatedControls: riskControlIds, // DED-1964: Inherit related controls from risk
        selectedControls: riskControlIds,
        procRef: riskControlIds, // Also set procRef for compatibility
        comments: comments,
        description: comments,
        confluenceURL: '', // Required field
        componentId: '', // Will be set by createDocument
        docHash: '', // Will be generated by the system
        updateMethod: 'create',
        edit: false,
      } as any;

      // Create new document with file
      const savedDoc = await createDocument(documentData as any, selectedFile);
      
      if (savedDoc?.docID && onDocumentsChange) {
        const newLinkedDocs = [...linkedDocuments, savedDoc.docID];
        onDocumentsChange(newLinkedDocs);
      }

      toast.success('Document uploaded and linked successfully');
      setShowUploadDialog(false);
      setSelectedFile(null);
      setUploadFormData({
        title: '',
        documentType: 'Evidence',
        owner: '',
        comments: '',
      });
      fetchDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle document save callback
  const handleDocumentSave = async (savedDoc: any) => {
    toast.success('Document saved successfully');
    setIsDocumentDialogOpen(false);
    
    // Add the new document ID to linked documents
    if (savedDoc?.docID && onDocumentsChange) {
      const newLinkedDocs = [...linkedDocuments, savedDoc.docID];
      onDocumentsChange(newLinkedDocs);
    }
    
    // Refresh the documents list
    fetchDocuments();
  };

  // Handle unlinking a document
  const handleUnlinkDocument = async (docId: string) => {
    if (!onDocumentsChange) return;
    
    try {
      const newLinkedDocs = linkedDocuments.filter(id => id !== docId);
      onDocumentsChange(newLinkedDocs);
      toast.success('Document unlinked');
      setShowUnlinkDialog(false);
      setDocToUnlink(null);
    } catch (error) {
      console.error('Error unlinking document:', error);
      toast.error('Failed to unlink document');
    }
  };

  // Handle viewing/editing existing document
  const handleEditDocument = (docId: string) => {
    setPrefillData({
      documentID: docId,
      isEdit: true
    });
    setDialogKey(prev => prev + 1);
    setIsDocumentDialogOpen(true);
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-end gap-2 mb-4">
        {!isReadOnly && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLinkDialog(true)}
            >
              <Link className="h-4 w-4 mr-1" />
              Link Existing
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUploadDialog(true)}
            >
              <Upload className="h-4 w-4 mr-1" />
              Upload File
            </Button>
          </>
        )}
      </div>
      <div>
        {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading documents...</span>
                </div>
              ) : documents.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-10 w-10 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground">No linked documents</p>
                  {!isReadOnly && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Click "Add Document" to create a supporting document for this risk
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.docID}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-5 w-5 text-primary shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">
                            {doc.displayName || doc.fileName}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {doc.documentType && (
                              <Badge variant="outline" className="text-xs">
                                {doc.documentType}
                              </Badge>
                            )}
                            {doc.nextReviewDate && (
                              <span>
                                Next review: {renderDate(doc.nextReviewDate)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/documentdetail/${doc.docID}`, '_blank')}
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        {!isReadOnly && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setDocToUnlink(doc.docID);
                              setShowUnlinkDialog(true);
                            }}
                            title="Unlink document"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
      </div>

      {/* Document Create/Edit Dialog */}
      <Dialog open={isDocumentDialogOpen} onOpenChange={setIsDocumentDialogOpen}>
        <DialogContent
          className="w-screen h-screen max-w-none p-0 m-0 rounded-none bg-card/40"
          style={{
            width: '100vw',
            maxWidth: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          <div className="h-full">
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-auto p-4">
                <DialogDocumentDetail
                  key={dialogKey}
                  prefillData={prefillData}
                  onSave={handleDocumentSave}
                  onClose={() => setIsDocumentDialogOpen(false)}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unlink Confirmation Dialog */}
      <Dialog open={showUnlinkDialog} onOpenChange={setShowUnlinkDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Unlink Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to unlink this document from the risk? The document will not be deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlinkDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => docToUnlink && handleUnlinkDocument(docToUnlink)}
            >
              Unlink
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DED-1964: Upload File Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => {
        setShowUploadDialog(open);
        if (!open) {
          setSelectedFile(null);
          setUploadFormData({
            title: '',
            documentType: 'Evidence',
            owner: '',
            comments: '',
          });
        }
      }}>
        <DialogContent 
          className="max-w-6xl! w-6xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => {
                setShowUploadDialog(false);
                setSelectedFile(null);
                setUploadFormData({
                  title: '',
                  documentType: 'Evidence',
                  owner: '',
                  comments: '',
                });
              }}
              disabled={isUploading}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Header */}
            <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                <FontAwesomeIcon icon={faFileLines} className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">Upload Document</h2>
              <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                Upload a file to link to this risk. The document will inherit properties from the risk.
              </p>
            </div>

            {/* Form Content - scrollable */}
            <div className="p-8 flex-1 min-h-0 overflow-y-auto space-y-6">
              {/* File Upload */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <Label className="block text-sm font-semibold text-foreground mb-1">
                  File <span className="text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Select the document file to upload
                </p>
                
                {selectedFile ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <FileText className="w-8 h-8 text-emerald-500" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelectedFile(null)}
                      className="shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    onClick={() => document.getElementById('file-upload')?.click()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const file = e.dataTransfer.files?.[0];
                      if (file) {
                        setSelectedFile(file);
                        if (!uploadFormData.title) {
                          const displayName = file.name.replace(/\.[^/.]+$/, '');
                          setUploadFormData(prev => ({ ...prev, title: displayName }));
                        }
                      }
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    className="border-2 border-dashed border-primary/20 rounded-lg p-8 text-center cursor-pointer hover:border-primary/40 hover:bg-white/5 transition-all"
                  >
                    <FontAwesomeIcon icon={faUpload} className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PDF, DOC, DOCX, or other document formats
                    </p>
                  </div>
                )}
                
                <input
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      // Auto-populate title from filename if not already set
                      if (!uploadFormData.title) {
                        const displayName = file.name.replace(/\.[^/.]+$/, '');
                        setUploadFormData(prev => ({ ...prev, title: displayName }));
                      }
                    }
                  }}
                  className="hidden"
                  id="file-upload"
                />
              </div>

              {/* Document Title */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <Label htmlFor="doc-title" className="block text-sm font-semibold text-foreground mb-1">
                  Document Title <span className="text-destructive">*</span>
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  A clear, descriptive name for this document
                </p>
                <Input
                  id="doc-title"
                  placeholder="e.g., Risk Assessment Report"
                  value={uploadFormData.title}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50"
                />
              </div>

              {/* Document Type */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <Label htmlFor="doc-type" className="block text-sm font-semibold text-foreground mb-1">
                  Document Type
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Select the type of document
                </p>
                <Select
                  value={uploadFormData.documentType}
                  onValueChange={(value) => setUploadFormData(prev => ({ ...prev, documentType: value }))}
                  disabled={loadingDocTypes}
                >
                  <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full rounded-lg">
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.key}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Owner */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
                <Label className="block text-sm font-semibold text-foreground mb-1">
                  Document Owner
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  {riskOwner && !uploadFormData.owner ? `Default: ${riskOwner} (from risk)` : 'Select the document owner'}
                </p>
                <SingleUserCombobox
                  selected={uploadFormData.owner || riskOwner || ''}
                  setSelected={(value) => setUploadFormData(prev => ({ ...prev, owner: value }))}
                  placeholder="Select owner..."
                  className="bg-white/5 border-primary/10 h-11"
                />
              </div>

              {/* Comments */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                <Label htmlFor="doc-comments" className="block text-sm font-semibold text-foreground mb-1">
                  Comments
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Add any additional notes or context
                </p>
                <Input
                  id="doc-comments"
                  placeholder={`Supporting document for risk ${riskId}`}
                  value={uploadFormData.comments}
                  onChange={(e) => setUploadFormData(prev => ({ ...prev, comments: e.target.value }))}
                  className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50"
                />
              </div>

              {/* Inherited Properties Info */}
              <div className="rounded-lg bg-primary/5 border border-primary/10 p-4">
                <p className="text-sm font-medium text-foreground mb-2">Inherited from risk:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-primary" />
                    Related Controls: {riskControlIds.length > 0 ? `${riskControlIds.length} control(s)` : 'None'}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-primary" />
                    Last Review Date: Today
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-3 w-3 text-primary" />
                    Next Review Date: One year from today
                  </li>
                </ul>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-white/10 px-8 py-5 bg-card flex justify-between items-center shrink-0">
              <p className="text-xs text-muted-foreground">
                {!selectedFile 
                  ? "Select a file to continue" 
                  : !uploadFormData.title.trim()
                    ? "Enter a document title to continue"
                    : "Ready to upload document"}
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowUploadDialog(false);
                    setSelectedFile(null);
                    setUploadFormData({
                      title: '',
                      documentType: 'Evidence',
                      owner: '',
                      comments: '',
                    });
                  }} 
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button 
                  disabled={!selectedFile || isUploading} 
                  onClick={handleFileUpload} 
                  className="min-w-[140px]"
                >
                  {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isUploading ? 'Uploading...' : 'Upload & Link'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Existing Documents Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={(open) => {
        setShowLinkDialog(open);
        if (!open) setSelectedDocsToLink([]);
      }}>
        <DialogContent className="sm:max-w-[500px] bg-card">
          <DialogHeader>
            <DialogTitle>Link Existing Documents</DialogTitle>
            <DialogDescription>
              Select documents from your document library to link to this risk.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <DocumentsProvider>
              <DocumentsCombobox
                selected={selectedDocsToLink}
                setSelected={setSelectedDocsToLink}
                excludeIds={linkedDocuments}
                placeholder="Search and select documents..."
                fullWidth
              />
            </DocumentsProvider>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowLinkDialog(false);
              setSelectedDocsToLink([]);
            }}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (selectedDocsToLink.length > 0 && onDocumentsChange && activeTenancy?.id) {
                  try {
                    // DED-1507: Append related controls from risk to each linked document
                    for (const docId of selectedDocsToLink) {
                      try {
                        const docData = await getDocument(docId, activeTenancy.id);
                        if (docData) {
                          // Merge risk controls with existing document controls
                          const existingControls = Array.isArray(docData.selectedControls) 
                            ? docData.selectedControls 
                            : Array.isArray(docData.relatedControls) 
                              ? docData.relatedControls 
                              : [];
                          const mergedControls = Array.from(new Set([...existingControls, ...riskControlIds]));
                          
                          // Update document with merged controls and linked risk
                          const existingLinkedRisks = Array.isArray((docData as any).linkedRisks) 
                            ? (docData as any).linkedRisks 
                            : [];
                          const updatedLinkedRisks = Array.from(new Set([...existingLinkedRisks, riskId]));
                          
                          // Save the updated document
                          await saveDocument({
                            ...docData,
                            selectedControls: mergedControls,
                            relatedControls: mergedControls,
                            linkedRisks: updatedLinkedRisks,
                          } as any, null, activeTenancy.id);
                          
                          console.log(`[RiskLinkedDocuments] Updated document ${docId} with controls:`, mergedControls);
                        }
                      } catch (err) {
                        console.error(`[RiskLinkedDocuments] Error updating document ${docId}:`, err);
                      }
                    }
                    
                    const newLinkedDocs = [...linkedDocuments, ...selectedDocsToLink];
                    // Immediately fetch and display the new documents
                    await fetchDocuments(newLinkedDocs);
                    // Then persist to backend
                    await onDocumentsChange(newLinkedDocs);
                    toast.success(`Linked ${selectedDocsToLink.length} document(s)`);
                    setShowLinkDialog(false);
                    setSelectedDocsToLink([]);
                  } catch (error) {
                    console.error('[RiskLinkedDocuments] Error linking documents:', error);
                    toast.error('Failed to link documents');
                  }
                }
              }}
              disabled={selectedDocsToLink.length === 0}
            >
              Link {selectedDocsToLink.length > 0 ? `(${selectedDocsToLink.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RiskLinkedDocuments;
